/**
 * Load-matching comparison API client (ST-00218 P2; contract: scratchpad/st00218/API.md;
 * server: server/routes/loadMatching.ts at /api/load-matching). Shapes: `types/loadMatching`.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh). Resolves to the response BODY.
 *
 *   getStatus()                               → LoadMatchingStatusResponse   (works when off)
 *   getCompare({startDate,endDate,groupingIds}) → CompareResponse            (503 when off)
 *   getRoute(id, {startDate,endDate})         → RouteDrawerResponse
 *   setWindowOverride(id, minutes | null, note?) → WindowUpdateResponse    (manage)
 *   signOff({groupingIds,…})                  → SignoffCreateResponse      (manage)
 *   listSignoffs({groupingId?,batchId?})      → { signoffs }
 *   switchRoute(id, note?) / revertRoute(id, note) → SwitchResponse          (manage)
 *   downloadExport(…) / downloadSignoffExport(…) / downloadBatchExport(…)   → the file name saved
 *
 * The comparison reads are slow on a month of data (≈ 6–8 s, longer for a year), so they get a
 * long timeout. `isSwitchedOff(err)` recognises the 503 LOAD_MATCHING=off answer.
 */
import api from './api';
import type {
  CompareResponse,
  LoadMatchingStatusResponse,
  RouteDrawerResponse,
  SignoffCreateResponse,
  SignoffRecord,
  SwitchResponse,
  WindowUpdateResponse,
} from '../types/loadMatching';

const BASE = '/load-matching';
const READ_TIMEOUT = 180000;

export interface PeriodParams {
  startDate: string;
  endDate: string;
}

export interface CompareParams extends PeriodParams {
  /** Omitted / empty = every active route of the tenant. */
  groupingIds?: number[];
}

export type ExportFormat = 'csv' | 'pdf';

interface ApiErrorLike {
  response?: { status?: number; data?: unknown; headers?: Record<string, string> };
  message?: string;
}

const errBody = (err: unknown): { error?: string; message?: string } => {
  const data = (err as ApiErrorLike | null)?.response?.data;
  return data && typeof data === 'object' ? (data as { error?: string; message?: string }) : {};
};

/** The 503 the API answers on every read while LOAD_MATCHING=off. */
export function isSwitchedOff(err: unknown): boolean {
  const e = err as ApiErrorLike | null;
  return e?.response?.status === 503 && errBody(err).error === 'load_matching_off';
}

/** The API's error code (`signoff_required`, `already_engine`, `load_matching_off` …), else null. */
export function loadMatchingErrorCode(err: unknown): string | null {
  return errBody(err).error ?? null;
}

/** A sentence for the user: the API's message, else `fallback` (403 / 503 have their own words). */
export function loadMatchingErrorText(err: unknown, fallback: string): string {
  const e = err as ApiErrorLike | null;
  const status = e?.response?.status;
  if (status === 403) return 'You do not have permission for this.';
  if (isSwitchedOff(err)) return 'Load matching is switched off on this server.';
  if (status === 503 && errBody(err).error === 'ledger_unavailable')
    return 'Load matching is not set up on this server yet (the database update has not run).';
  const body = errBody(err);
  if (body.message && body.error !== 'internal_error') return body.message;
  if (e?.message && /timeout/i.test(e.message))
    return 'This took too long. Try a shorter period or fewer routes.';
  return fallback;
}

const idsParam = (ids?: number[]): string | undefined =>
  ids && ids.length ? ids.join(',') : undefined;

export async function getStatus(): Promise<LoadMatchingStatusResponse> {
  const res = await api.get(`${BASE}/status`);
  return res.data as LoadMatchingStatusResponse;
}

export async function getCompare(
  params: CompareParams,
  config: { signal?: AbortSignal } = {}
): Promise<CompareResponse> {
  const res = await api.get(`${BASE}/compare`, {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      groupingIds: idsParam(params.groupingIds),
    },
    timeout: READ_TIMEOUT,
    signal: config.signal,
  });
  return res.data as CompareResponse;
}

export async function getRoute(
  groupingId: number,
  params: PeriodParams & { limit?: number },
  config: { signal?: AbortSignal } = {}
): Promise<RouteDrawerResponse> {
  const res = await api.get(`${BASE}/routes/${groupingId}`, {
    params: { startDate: params.startDate, endDate: params.endDate, limit: params.limit },
    timeout: READ_TIMEOUT,
    signal: config.signal,
  });
  return res.data as RouteDrawerResponse;
}

/** Set (minutes, 60–4320) or clear (null) the route's window; the server logs a `window` record. */
export async function setWindowOverride(
  groupingId: number,
  overrideMinutes: number | null,
  note?: string | null
): Promise<WindowUpdateResponse> {
  const res = await api.put(`${BASE}/routes/${groupingId}/window`, {
    overrideMinutes,
    ...(note ? { note } : {}),
  });
  return res.data as WindowUpdateResponse;
}

export interface SignoffInput extends PeriodParams {
  groupingIds: number[];
  signedByName: string;
  signedByTitle?: string | null;
  note?: string | null;
}

export async function signOff(input: SignoffInput): Promise<SignoffCreateResponse> {
  const res = await api.post(
    `${BASE}/signoffs`,
    {
      groupingIds: input.groupingIds,
      startDate: input.startDate,
      endDate: input.endDate,
      signedByName: input.signedByName,
      signedByTitle: input.signedByTitle || undefined,
      note: input.note || undefined,
    },
    // One drawer build per route on the server (≈ 1–3 s each).
    { timeout: READ_TIMEOUT }
  );
  return res.data as SignoffCreateResponse;
}

export async function listSignoffs(
  params: { groupingId?: number; batchId?: string; limit?: number } = {}
): Promise<SignoffRecord[]> {
  const res = await api.get(`${BASE}/signoffs`, { params });
  return (res.data as { signoffs: SignoffRecord[] }).signoffs;
}

export async function switchRoute(groupingId: number, note?: string): Promise<SwitchResponse> {
  const res = await api.post(`${BASE}/routes/${groupingId}/switch`, note ? { note } : {});
  return res.data as SwitchResponse;
}

export async function revertRoute(groupingId: number, note: string): Promise<SwitchResponse> {
  const res = await api.post(`${BASE}/routes/${groupingId}/revert`, { note });
  return res.data as SwitchResponse;
}

function filenameOf(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const plain = /filename="([^"]+)"/i.exec(disposition);
  return plain?.[1] || fallback;
}

/** GET a document and save it; a JSON error inside the blob is turned back into the error body. */
async function download(
  url: string,
  params: Record<string, string | undefined>,
  fallback: string
): Promise<string> {
  let res;
  try {
    res = await api.get(url, { params, responseType: 'blob', timeout: READ_TIMEOUT });
  } catch (err) {
    const e = err as ApiErrorLike;
    const data = e?.response?.data;
    if (data instanceof Blob && e.response) {
      try {
        e.response.data = JSON.parse(await data.text()) as unknown;
      } catch {
        /* not JSON */
      }
    }
    throw err;
  }
  const blob = res.data as Blob;
  const name = filenameOf(
    (res.headers as Record<string, string> | undefined)?.['content-disposition'],
    fallback
  );
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  }
  return name;
}

/** The document for review, from live figures (1–20 routes). */
export function downloadExport(
  params: PeriodParams & { groupingIds: number[]; format: ExportFormat }
): Promise<string> {
  return download(
    `${BASE}/export`,
    {
      format: params.format,
      startDate: params.startDate,
      endDate: params.endDate,
      groupingIds: idsParam(params.groupingIds),
    },
    `load-matching_${params.startDate}_${params.endDate}.${params.format}`
  );
}

/** The document as signed (rebuilt from the stored snapshot). */
export function downloadSignoffExport(signoffId: number, format: ExportFormat): Promise<string> {
  return download(
    `${BASE}/signoffs/${signoffId}/export`,
    { format },
    `load-matching_signoff_${signoffId}.${format}`
  );
}

/** Every route of one sign-off batch in one document. */
export function downloadBatchExport(batchId: string, format: ExportFormat): Promise<string> {
  return download(
    `${BASE}/signoffs/batch/${encodeURIComponent(batchId)}/export`,
    { format },
    `load-matching_batch.${format}`
  );
}
